import { useMemo, useState } from "react";
import { IStoreFilterableOption } from "@/services/store/select-options";
import { MultiOption, PanelStore } from "./types";

/**
 * Lógica do painel de filtro de lojas da listagem (multi-seleção).
 * Estado e Regional são multi-select e se encadeiam: estado filtra as regionais
 * disponíveis, que por sua vez filtram a lista de lojas (junto com a busca textual).
 * As opções são derivadas da própria lista de lojas (só aparece o que existe).
 */
export function useStoreMultiFilter(stores: IStoreFilterableOption[]) {
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [selectedRegionals, setSelectedRegionals] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  const allStores = useMemo<PanelStore[]>(
    () =>
      stores.map(store => ({
        code: parseInt(store.value),
        name: store.label,
        state: store.state ?? "",
        city: store.city ?? "",
        regionalId: store.regionalId,
        regionalName: store.regionalName,
      })),
    [stores],
  );

  const stateOptions = useMemo<MultiOption<string>[]>(() => {
    const distinct = Array.from(
      new Set(allStores.map(store => store.state).filter(Boolean)),
    ).sort((a, b) => a.localeCompare(b));
    return distinct.map(uf => ({ value: uf, label: uf }));
  }, [allStores]);

  // Regionais disponíveis dependem dos estados selecionados (cascata).
  const regionalOptions = useMemo<MultiOption<number>[]>(() => {
    const base = selectedStates.length
      ? allStores.filter(store => selectedStates.includes(store.state))
      : allStores;
    const byId = new Map<number, string>();
    base.forEach(store => {
      if (store.regionalId != null && store.regionalName) {
        byId.set(store.regionalId, store.regionalName);
      }
    });
    return Array.from(byId.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allStores, selectedStates]);

  const filteredStores = useMemo<PanelStore[]>(() => {
    const term = searchTerm.trim().toLowerCase();
    return allStores
      .filter(store => {
        const matchesState =
          !selectedStates.length || selectedStates.includes(store.state);
        const matchesRegional =
          !selectedRegionals.length ||
          (store.regionalId != null && selectedRegionals.includes(store.regionalId));
        const matchesSearch =
          !term ||
          store.name.toLowerCase().includes(term) ||
          store.code.toString().includes(term);
        return matchesState && matchesRegional && matchesSearch;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allStores, selectedStates, selectedRegionals, searchTerm]);

  const toggleState = (uf: string) => {
    setSelectedStates(prev =>
      prev.includes(uf) ? prev.filter(item => item !== uf) : [...prev, uf],
    );
    // Remove regionais que deixaram de existir sob os estados selecionados.
    setSelectedRegionals(prevRegionals => {
      const nextStates = selectedStates.includes(uf)
        ? selectedStates.filter(item => item !== uf)
        : [...selectedStates, uf];
      if (!nextStates.length) return prevRegionals;
      const available = new Set(
        allStores
          .filter(store => nextStates.includes(store.state))
          .map(store => store.regionalId)
          .filter((id): id is number => id != null),
      );
      return prevRegionals.filter(id => available.has(id));
    });
  };

  const toggleRegional = (id: number) => {
    setSelectedRegionals(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id],
    );
  };

  const clearStates = () => {
    setSelectedStates([]);
  };

  const clearRegionals = () => {
    setSelectedRegionals([]);
  };

  const clearAllFilters = () => {
    setSelectedStates([]);
    setSelectedRegionals([]);
    setSearchTerm("");
  };

  const hasActiveFilters =
    selectedStates.length > 0 ||
    selectedRegionals.length > 0 ||
    searchTerm.trim().length > 0;

  const statesSummary = selectedStates.join(", ");
  const regionalsSummary = regionalOptions
    .filter(option => selectedRegionals.includes(option.value))
    .map(option => option.label)
    .join(", ");

  return {
    allStores,
    stateOptions,
    regionalOptions,
    filteredStores,
    selectedStates,
    selectedRegionals,
    searchTerm,
    setSearchTerm,
    toggleState,
    toggleRegional,
    clearStates,
    clearRegionals,
    clearAllFilters,
    hasActiveFilters,
    statesSummary,
    regionalsSummary,
  };
}
